import * as React from 'react';
import type { EntityFormControlOwnProps } from '../../types/controls';
import { PrimitiveControlFormField } from '../primitive-control-form-field/primitive-control-form-field.component';
import { useEntityFormControl } from '../../hooks/use-entity-form-control/use-entity-form-control';
import { Hidden } from '../hidden/hidden.component';
import { LocalizedEntityFormControl } from './localized-entity-form-control/localized-entity-form-control.component';

/*
 * EntityFormControl encapsulates taking in the JsonForms props, calling useEntityFormControl to get the correct annotation/props, and passing those to the individual controls. The renderControl prop lets you completely take over how the form control is rendered.
 *
 * Localized controls are wrapped in the LocalizedEntityFormControl component, which handles the localized value and change handling.
 * and we allow it to complete the rendering logic (including any
 * wrapper component or omitting of the default wrapper component).
 *
 * Optional wrapperComponent that can be passed to override the default PrimitiveControlFormField for the control
 * omitWrapperComponent as true if no wrapper is needed.
 *
 */

export const EntityFormControl: React.FC<EntityFormControlOwnProps> = ({
  omitWrapperComponent = false,
  wrapperComponent: DefaultWrapperComponent = PrimitiveControlFormField,
  renderControl,
  ...props
}) => {
  const entityFormControlProps = useEntityFormControl(props);
  const { hidden } = entityFormControlProps;

  if (hidden) return <Hidden {...entityFormControlProps} />;

  if (!renderControl) return null;

  if (entityFormControlProps.localized) {
    return (
      <LocalizedEntityFormControl
        {...entityFormControlProps}
        omitWrapperComponent={omitWrapperComponent}
        wrapperComponent={DefaultWrapperComponent}
        renderControl={renderControl}
      >
        {renderControl(entityFormControlProps)}
      </LocalizedEntityFormControl>
    );
  }

  return omitWrapperComponent ? (
    <>{renderControl(entityFormControlProps)}</>
  ) : (
    <DefaultWrapperComponent {...entityFormControlProps}>
      {renderControl(entityFormControlProps)}
    </DefaultWrapperComponent>
  );
};
